import { useCallback, useEffect, useMemo, useState } from "react";

import { PropTypes } from "mobx-react"
import { FormSection, Col, FormGroup, Row, Label, Switch, Button, InputGroup, LoadingSpinner, DefaultAlert, Input, Separator, SimpleAlert, FormCard } from 'twyne-new.ui'

import ConfirmationPopover from '@Components/ConfirmationPopover';
import Select from "@Components/form/Select";

const propTypes = {
  id: PropTypes.number,
  authentication: PropTypes.object,
}

export default function TwoFactorAuthentication({ id, data, useUserStore, type }) {
  const { id: authenticationId, isValid: isValidated, method: authenticationMethod } = data || {}
  const { createAuthentication, validateAuthentication, disableAuthentication } = useUserStore()

  const [active, setActive] = useState(false)
  const [method, setMethod] = useState('')
  const [qrcode, setQrcode] = useState(null)
  const [emailSent, setEmailSent] = useState(false)
  const [isValid, setIsValid] = useState(null)

  useEffect(() => {
    if (isValidated) setActive(isValidated)
  }, [isValidated])

  const handleActive = useCallback(event => {
    const active = event.target.checked
    setActive(active)
    setMethod(active ? 'authenticator' : '')
    if (!active) {
      setQrcode(null)
      setEmailSent(false)
      setIsValid(null)
    }
  }, [])

  const handleDisable = useCallback(async () => {
    await disableAuthentication(authenticationId, id)
  }, [authenticationId, id, disableAuthentication])

  const handleMethod = useCallback(async option => {
    setMethod(option?.value)
  }, [])

  const fetchCode = useCallback(async () => {
    if (method) {
      const response = await createAuthentication({ userId: id, method })
      if (method === 'email') {
        setEmailSent(response)
        setQrcode(null)
      } else {
        setEmailSent(false)
        setQrcode(response)
      }
    }
  }, [id, method, createAuthentication])

  const sendConfirmation = useCallback(async confirmation => {
    const result = await validateAuthentication({ userId: id, confirmation })
    setIsValid(result)
  }, [id, validateAuthentication])

  if (isValidated) {
    const ActiveButton = () => <div className='flex items-center gap-2'>
      Active
      <Switch value={true} />
    </div>

    return <>
      <FormCard
        title='Two-factor authentication'
        render
        headerButtons={<>
          <ConfirmationPopover id={id} title="Disable Authentication" target={<ActiveButton />}>
            <p className='font-bold'>Are you sure you want to disable two-factor authentication?</p>
            <Button color="danger" onClick={handleDisable} block className="flex justify-center mt-2">Yes, I am sure</Button>
          </ConfirmationPopover>
        </>}
      >
        <SimpleAlert variant='success' className='my-0!'>
          Two-factor authentication by {authenticationMethod} enabled.
        </SimpleAlert>
      </FormCard>
    </>
  }

  return <>
    <Wrapper type={type} active={active} onChangeActive={handleActive}>
      <Row className='relative'>
        <Method value={method} disabled={!active} onChange={handleMethod} onClick={fetchCode} />
        <Authenticator qrcode={qrcode} onClick={sendConfirmation} />
        <Email emailSent={emailSent} onClick={sendConfirmation} />
        <ErrorAlert isValid={isValid} />
      </Row>
    </Wrapper>
  </>
}

function Wrapper({ type, active, onChangeActive, children }) {
  if (type === 'card') {
    return <FormCard
      title='Two-factor authentication'
      render={active}
      headerButtons={<Active value={active} onChange={onChangeActive} />}
    >
      {children}
    </FormCard>
  }

  return <>
    <FormSection titleClass='flex items-center justify-between'>
      Two-factor authentication
      <div className='text-base twy-text-base'>
        <Active value={active} onChange={onChangeActive} />
      </div>
    </FormSection>
    <Separator className='my-2' />
    {children}
  </>
}

function Active({ value, onChange }) {
  return <div className='flex items-center gap-2'>
    Active
    <Switch value={value} onChange={onChange} />
  </div>
}

function Method({ value, onChange, onClick, disabled }) {
  const [isLoading, setIsLoading] = useState(false)

  const options = useMemo(() => [
    { label: "E-mail", value: 'email' },
    { label: "Authenticator", value: 'authenticator' },
  ], [])

  const handleClick = useCallback(async () => {
    setIsLoading(true)
    await onClick()
    setIsLoading(false)
  }, [onClick])

  if (disabled) {
    return null
  }

  return <Col size="6">
    <FormGroup>
      <Label>Method</Label>
      <InputGroup>
        <Select name="_" clearable={false} value={value}
          options={options} handleOnChange={onChange} />
        <Button className='flex items-center rounded-l-none' color="accent" onClick={handleClick} disabled={!value}>
          {isLoading ? <LoadingSpinner width={18} height={18} /> : value === 'email' ? 'Send email' : 'Get QR-Code'}
        </Button>
      </InputGroup>
    </FormGroup>
  </Col>
}

function Authenticator({ qrcode, onClick }) {
  if (!qrcode) {
    return null
  }

  return <>
    <Col size="6" className="absolute top-0 right-8">
      <FormGroup>
        <Label>QR-Code</Label>
        <img className="border twy-border-base w-full" src={qrcode} />
      </FormGroup>
    </Col>
    <Col size="12">
      <Row>
        <Col size="6">
          <FormGroup>
            <Label>6-digit Code</Label>
            <Confirmation onClick={onClick} />
          </FormGroup>
        </Col>
      </Row>
    </Col>
    <Col size="12">
      <Row>
        <Col size="6">
          <FormGroup>
            <Label>Steps</Label>
            <DefaultAlert variant='warning' icon={false} className='list-decimal'>
              <ol className='list-decimal pl-2'>
                <li>Download an <DownloadApp>authenticator app</DownloadApp></li>
                <li>Scan the QR-Code</li>
                <li>Enter the 6-digit code</li>
              </ol>
            </DefaultAlert>
          </FormGroup>
        </Col>
      </Row>
    </Col>
  </>
}

function Email({ emailSent, onClick }) {
  if (!emailSent) {
    return null
  }

  return <>
    <Col size="6">
      <FormGroup>
        <Label>6-digit Code</Label>
        <Confirmation onClick={onClick} />
      </FormGroup>
    </Col>
    <Col size="6">
      <FormGroup>
        <Label>Steps</Label>
        <DefaultAlert variant='warning' icon={false} className='list-decimal'>
          <ol className='list-decimal pl-2'>
            <li>The activation code has been sent, check your e-mail inbox</li>
            <li>Enter the 6-digit code</li>
          </ol>
        </DefaultAlert>
      </FormGroup>
    </Col>
  </>
}

function Confirmation({ onClick }) {
  const [isLoading, setIsLoading] = useState(false)
  const [confirmation, setConfirmation] = useState('')
  const isValid = confirmation.length === 6

  const handleClick = useCallback(async confirmation => {
    setIsLoading(true)
    await onClick(confirmation)
    setIsLoading(false)
  }, [onClick])

  const handleChange = useCallback(async event => {
    setConfirmation(event.target.value)
  }, [])

  return <InputGroup>
    <Input required name="_" type="number" value={confirmation} onChange={handleChange} />
    <Button color="accent" onClick={() => handleClick(confirmation)} disabled={!isValid}>
      {isLoading ? <LoadingSpinner width={18} height={18} /> : 'Confirm'}
    </Button>
  </InputGroup>
}

function DownloadApp({ children }) {
  return <ConfirmationPopover
    title={<span className='font-bold text-lg'>Download App</span>}
    trigger={<span className='twy-text-accent cursor-pointer'>{children}</span>}
    contentProps={{ side: 'top' }}
  >
    <span className='font-bold'>Google Authenticator</span>
    <div>
      <a href={GOOGLE.playstore} className='twy-text-accent' target='_blank' rel="noreferrer">PlayStore </a>
      /
      <a href={GOOGLE.appstore} className='twy-text-accent' target='_blank' rel="noreferrer"> AppStore</a>
    </div>

    <Separator className='my-2' />

    <span className='font-bold'>Microsoft Authenticator</span>
    <div>
      <a href={MICROSOFT.playstore} className='twy-text-accent' target='_blank' rel="noreferrer">PlayStore </a>
      /
      <a href={MICROSOFT.appstore} className='twy-text-accent' target='_blank' rel="noreferrer"> AppStore</a>
    </div>
  </ConfirmationPopover>
}

function ErrorAlert({ isValid }) {
  if (isValid === false) {
    return <Col size="12">
      <SimpleAlert variant='danger'>
        Incorrect confirmation or code expired, please try again!
      </SimpleAlert>
    </Col>
  }

  return null
}

const GOOGLE = {
  playstore: "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pcampaignid=web_share",
  appstore: "https://apps.apple.com/br/app/google-authenticator/id388497605?platform=iphone",
}

const MICROSOFT = {
  playstore: "https://play.google.com/store/apps/details?id=com.azure.authenticator&pcampaignid=web_share",
  appstore: "https://apps.apple.com/br/app/microsoft-authenticator/id983156458",
}

TwoFactorAuthentication.propTypes = propTypes
TwoFactorAuthentication.defaultProps = {}
